import React, { useEffect } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { WorldPageHeader } from '../../components/world/WorldPageHeader';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { ValidationBadge, ValidationSummary } from '../../components/progression/ValidationBadge';
import { NpcCard } from '../../components/progression/NpcCard';
import { ChecklistBar } from '../../components/progression/ChecklistBar';
import { MaterialList } from '../../components/progression/MaterialList';
import { ItemLevelExplainer } from '../../components/progression/ItemLevelExplainer';
import { GuidePanel, FactList, Note } from '../../components/progression/GuidePanel';
import { RecipeSteps, ClassicRecipeLabel } from '../../components/progression/RecipeSteps';
import { getRecipe, recipePath, recipeIngredients } from '../../data/guide/recipes';
import { recipeAuditOf } from '../../data/guide/sourceAudit';
import { ConfigAuditPanel } from '../../components/progression/ConfigAuditPanel';
import { useChecklist } from '../../hooks/useChecklist';
import { useWorldLookup } from '../../hooks/useWorldLookup';
import { useHashScroll } from '../../hooks/useHashScroll';
import { useNpcLocation } from '../../hooks/useNpcLocation';
import { npcLocationValidation } from '../../utils/npcLocation';
import { itemPath } from '../../utils/worldFormat';
import { summarizeMaterials, type WorldLookup } from '../../utils/progressionLookup';
import type { Recipe, RecipeResult } from '../../types/progression';
export function RecipePage() {
  const {
    recipeId
  } = useParams();
  const r = getRecipe(recipeId);
  if (!r) return <Navigate to="/guia/combinaciones" replace data-id="element-2502" data-mp="ybzxmumsd1ib" />;
  return <RecipeView key={r.id} r={r} data-id="element-2503" data-mp="ydkuiqwkowjc" />;
}
function RecipeView({
  r
}: {
  r: Recipe;
}) {
  const [params] = useSearchParams();
  const from = getRecipe(params.get('desde') ?? undefined);
  const list = useChecklist(`receta-${r.id}`);
  const {
    status,
    lookup
  } = useWorldLookup();
  useHashScroll();
  const allIngredients = recipeIngredients(r).filter(m => m.gameName || m.itemId);
  const sum = summarizeMaterials(lookup, allIngredients);
  const isWings = !r.group || r.group === 'alas';
  const parent = isWings ? {
    label: 'Alas y capas',
    to: '/guia/alas'
  } : {
    label: 'Combinaciones',
    to: '/guia/combinaciones'
  };
  const machine = r.machine ?? 'Chaos Machine';
  const npcLoc = useNpcLocation(r.npc);
  const npcRow = npcLoc ? npcLocationValidation(npcLoc) : {
    status: 'pendiente' as const,
    detail: ''
  };
  useEffect(() => {
    document.title = `${r.shortTitle} · ${parent.label} · Guía MU Vanguard`;
  }, [r, parent.label]);
  const groups = r.variants ? r.variants.map(v => ({
    prefix: v.id,
    ingredients: v.ingredients
  })) : [{
    prefix: 'ing',
    ingredients: r.ingredients
  }];
  const keys = groups.flatMap(g => g.ingredients.map(m => `${g.prefix}:${m.id}`));
  const done = keys.filter(k => list.isChecked(k)).length;
  const needsLevel = recipeIngredients(r).some(m => m.requirement?.includes('opción adicional'));
  const prev = getRecipe(r.prev);
  const next = getRecipe(r.next);
  // Misma clave que el informe de pendientes; la capa del RF se responde con Cloak of Warrior.
  const auditKeys: {
    key: string;
    title: string;
  }[] = r.variants ? r.variants.map(v => ({
    key: v.id === 'rf' ? 'cloak-of-warrior' : `${r.id}:${v.id}`,
    title: v.title
  })) : [{
    key: r.id,
    title: 'Qué documenta la configuración'
  }, ...(r.id === 'alas-evo-3' ? [{
    key: 'capes-third',
    title: 'Capas de tercera generación'
  }] : [])];
  const auditBlocks = auditKeys.flatMap(a => {
    const fact = recipeAuditOf(a.key);
    return fact ? [{
      ...a,
      fact
    }] : [];
  });
  return <div className="space-y-8 md:space-y-10" data-id="element-2504" data-mp="1gse054j4x5zo">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, parent, {
      label: r.shortTitle
    }]} eyebrow={`Receta · ${machine}`} title={r.title} data-id="element-2505" data-mp="1gr2suuqhdg3t">
        <p className="mt-2 max-w-2xl text-sm text-gray-300 md:text-base" data-id="element-2506" data-mp="299q8vvqme4y1">{r.summary}</p>
        <div className="mt-3" data-id="element-2507" data-mp="llofwug4fbdr">
          <ClassicRecipeLabel data-id="element-2508" data-mp="lly5igx97gix" />
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-id="element-2509" data-mp="gm20qfgqfl2b">
          {from && <Link to={recipePath(from.id)} className="inline-flex h-10 items-center gap-2 rounded-full bg-[#D4AF37] px-4 text-sm font-semibold text-[#050b14] hover:bg-[#F3E5AB]" data-id="element-2510" data-mp="go519tfigwla">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-2511" data-mp="7myhnbhkf5nt" />
              Volver a {from.shortTitle}
            </Link>}
          <CopyLinkButton data-id="element-2512" data-mp="14dhauuer36o4" />
        </div>
      </WorldPageHeader>

      <FactList facts={[{
      label: 'Dónde se combina',
      value: machine,
      status: 'clasica'
    }, {
      label: 'Zen',
      value: r.zen,
      status: r.zenStatus
    }, {
      label: 'Probabilidad de éxito',
      value: 'Se publicará solo con la fórmula confirmada',
      status: 'pendiente'
    }, {
      label: 'Si la combinación falla',
      value: r.failure,
      status: r.failureStatus
    }]} data-id="element-2513" data-mp="zd38nsztyqy0" />
      <NpcCard npcId={r.npc} lookup={lookup} data-id="element-2514" data-mp="1i9umhmwlsduo" />

      <ChecklistBar list={list} total={keys.length} done={done} data-id="element-2515" data-mp="30yff2zxjncmk" />

      {r.variants ? r.variants.map(v => <GuidePanel key={v.id} id={`capa-${v.id}`} eyebrow={v.who} title={v.title} data-id="element-2516" data-mp="35ya5z3hbk27m">
            <Note tone={v.id === 'rf' ? 'warn' : 'info'} data-id="element-2517" data-mp="39ykmha2qmb0q">{v.note}</Note>
            <MaterialList materials={v.ingredients} list={list} prefix={v.id} lookup={lookup} status={status} fromRecipe={r.id} data-id="element-2518" data-mp="3exxa3x1fnm4d" />
            <Results results={v.results} lookup={lookup} data-id="element-2519" data-mp="3jxtbdoiqwzg8" />
          </GuidePanel>) : <>
          <GuidePanel id="ingredientes" title="Ingredientes" data-id="element-2520" data-mp="3outk676zcgb1">
            <MaterialList materials={r.ingredients} list={list} prefix="ing" lookup={lookup} status={status} fromRecipe={r.id} data-id="element-2521" data-mp="3tn36ld8vyy8k" />
          </GuidePanel>
          <GuidePanel id="resultado" title="Resultado posible" data-id="element-2522" data-mp="1iiu96o0s0a2">
            {r.resultNote && <Note data-id="element-2523" data-mp="6jg8x99tmc6t">{r.resultNote}</Note>}
            <Results results={r.results} lookup={lookup} data-id="element-2524" data-mp="6gainqpgh49i" />
          </GuidePanel>
        </>}

      {r.steps && r.steps.length > 0 && <GuidePanel id="pasos" title="Cómo hacerlo" data-id="element-2525" data-mp="27jl7l35io19j">
          <RecipeSteps steps={r.steps} data-id="element-2526" data-mp="27i0ap6vqc68i" />
        </GuidePanel>}

      {needsLevel && <ItemLevelExplainer data-id="element-2527" data-mp="22n32g269y0wo" />}

      {auditBlocks.length > 0 && <GuidePanel id="configuracion" title="Datos de la configuración" data-id="element-2528" data-mp="2hb7o9f0misjo">
          <div className="space-y-3" data-id="element-2529" data-mp="2cjpkkj77udv0">
            {auditBlocks.map(b => <ConfigAuditPanel key={b.key} fact={b.fact} title={b.title} missingTitle="Falta para la receta completa" data-id="element-2530" data-mp="2chmqno2y00l0" />)}
          </div>
        </GuidePanel>}

      <ValidationSummary rows={[{
      label: 'Identidad de los objetos',
      status: sum?.identity.status ?? 'pendiente',
      detail: sum?.identity.detail ?? 'Cargando datos…'
    }, {
      label: 'Dónde se obtienen los ingredientes',
      status: sum?.routes.status ?? 'pendiente',
      detail: sum?.routes.detail ?? 'Cargando datos…'
    }, {
      label: 'Ingredientes y cantidades en Vanguard',
      status: 'clasica',
      detail: r.source ? `Referencia: ${r.source}` : 'Referencia clásica de Season 6'
    }, {
      label: 'Zen',
      status: r.zenStatus === 'clasica' ? 'clasica' : 'pendiente',
      detail: r.zen
    }, {
      label: 'Probabilidad de éxito y posible cambio por VIP',
      status: 'pendiente'
    }, {
      label: 'Efectos del fallo',
      status: 'pendiente'
    }, {
      label: 'Ubicación del NPC',
      ...npcRow
    }, {
      label: 'NPC que realiza la combinación en Vanguard',
      status: 'pendiente',
      detail: r.npc === 'pet-trainer' ? 'Identidad: Trainer (226), unificado con Pet Trainer (234) por administración el 11/10/2026. Ubicación, ingredientes y tasas se verifican por separado.' : 'Pendiente de confirmar por administración'
    }, {
      label: 'Prueba dentro del juego',
      status: 'sin-probar'
    }]} data-id="element-2531" data-mp="2m8zd205z1yjn" />

      <nav aria-label="Recetas relacionadas" className="grid gap-2 sm:grid-cols-2" data-id="element-2532" data-mp="i37qaoe44zz8">
        {prev && <Link to={recipePath(prev.id)} className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-4 hover:border-[#D4AF37]/40" data-id="element-2533" data-mp="n2c1tloif9sq">
            <ArrowLeft className="h-4 w-4 text-gray-500 group-hover:text-[#D4AF37]" aria-hidden="true" data-id="element-2534" data-mp="36xjr2gjfzqpr" />
            <span data-id="element-2535" data-mp="3201or8zxk9qq"><span className="block text-xs text-gray-400" data-id="element-2536" data-mp="321cuhecxrrkh">Paso anterior</span><span className="font-cinzel font-bold text-white" data-id="element-2537" data-mp="2x78nww7l1ljx">{prev.shortTitle}</span></span>
          </Link>}
        {next && <Link to={recipePath(next.id)} className="group flex items-center justify-end gap-3 rounded-2xl border border-[#D4AF37]/30 bg-[#0A1628] p-4 text-right hover:border-[#D4AF37]/60 sm:col-start-2" data-id="element-2538" data-mp="2s7m9ijg0pz9j">
            <span data-id="element-2539" data-mp="2jb4u340mff7z"><span className="block text-xs text-gray-400" data-id="element-2540" data-mp="2e9nz1p4timzb">Siguiente</span><span className="font-cinzel font-bold text-white" data-id="element-2541" data-mp="2ea7e8y332x9n">{next.shortTitle}</span></span>
            <ArrowRight className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2542" data-mp="29c5m7nnojnm1" />
          </Link>}
      </nav>
    </div>;
}
function Results({
  results,
  lookup
}: {
  results: RecipeResult[];
  lookup?: WorldLookup;
}) {
  return <ul className="grid gap-2 sm:grid-cols-2" data-id="element-2543" data-mp="7hetqwhpjb2l">
      {results.map(x => {
      const ref = lookup?.identify(x);
      return <li key={x.gameName} className="flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-[#0A1628] p-3" data-id="element-2544" data-mp="k11x53uzcj6o">
            <span className="min-w-0" data-id="element-2545" data-mp="otbw19e2otl3">
              {ref ? <Link to={itemPath(ref.id)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-2546" data-mp="trwounqzwazi">{ref.name}</Link> : <span className="font-semibold text-white" data-id="element-2547" data-mp="1265o0aiaiwj">{x.gameName}</span>}
              {x.who && <span className="block text-xs text-gray-400" data-id="element-2548" data-mp="61st12k1tfj2">{x.who}</span>}
            </span>
            {lookup && (ref ? <ValidationBadge status="identificado" data-id="element-2549" data-mp="b26prog7931s" /> : <ValidationBadge status="pendiente" label="No identificado en el catálogo" data-id="element-2550" data-mp="ayspclr3j3wz" />)}
          </li>;
    })}
    </ul>;
}